import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Section } from '~/eds/src/components/Section/Section';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { FieldModelTitle } from '../../fields/shared/FieldModelTitle';
import { FieldModel } from '../../fields/types';
import { DEPRECATED_usePollModelProgress } from '../../hooks/DEPRECATED_usePollModelProgress';
import { Evaluate } from '../../ModelDetails/Evaluate';
import { ModelVersionInfo } from '../../ModelVersionInfo';
import { compareFieldModelConfig } from '../../utils/config';
import { testIsNonOptimizedFieldModel } from '../../utils/status';
import { PublishOptions } from './PublishOptions';

interface Props {
  fieldModel: FieldModel;
}

export const ReviewPublishFieldModel = ({ fieldModel }: Props) => {
  const intl = useIntl();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const [enablePolling, setEnablePolling] = useState<boolean>(true);
  const dispatch = useDispatch();

  const editModelConfirmed = testIsNonOptimizedFieldModel(fieldModel, [
    'suspended',
  ]);

  const modelId = fieldModel.id;

  const { data: model, isLoading: isLoadingModel } =
    api.endpoints.getPromptModel.useQuery({ modelId });

  const { id } = fieldModel;
  const lastPublishVersion = Number(
    model?.currentPublished?.version_number ?? 0,
  );

  const {
    data: previousModelVersion = null,
    isLoading: isPreviousModelVersionLoading,
  } = api.endpoints.DEPRECATED_getPromptModelVersion.useQuery(
    {
      modelId: id,
      version: lastPublishVersion,
      highlightMethod,
    },
    {
      skip: lastPublishVersion <= 0 || !lastPublishVersion,
    },
  );

  const { progress, isLoading: isLoadingProgress } =
    DEPRECATED_usePollModelProgress({
      modelId: fieldModel.id,
      publishStatus: fieldModel.publishStatus,
      enablePolling,
    });

  const { publishStatus } = progress;
  const currentConfig = fieldModel.config;
  const previousConfig = previousModelVersion?.config;

  const hasOnlyFiltersChanged = useMemo(() => {
    if (previousConfig && currentConfig) {
      const { hasFiltersChanged, hasInstructionsChanged } =
        compareFieldModelConfig(previousConfig, currentConfig);

      return hasFiltersChanged && !hasInstructionsChanged;
    }
    return false;
  }, [previousConfig, currentConfig]);

  useEffect(() => {
    dispatch(
      slices.xRay.actions.setPublishMode(
        hasOnlyFiltersChanged ? 'new_docs_in_scope' : 'all_docs_in_scope',
      ),
    );
  }, [hasOnlyFiltersChanged]);

  const items = [
    {
      title: intl.formatMessage({
        id: 'ReviewPublish.generalInformation',
        defaultMessage: 'General Information',
        description: 'Title for general information section',
      }),
      content: (
        <ModelVersionInfo
          modelId={fieldModel.id}
          version={fieldModel.version}
        />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'ReviewPublish.modelPerformance',
        defaultMessage: 'Model Performance',
        description: 'Title for model performance section',
      }),
      content: (
        <Evaluate
          readOnly
          mode="summary-only"
          modelId={fieldModel.id}
          testCases={fieldModel.testCases}
          version={fieldModel.version}
        />
      ),
    },
  ];

  return (
    <ContentContainer
      loadingContent={{
        isLoading:
          isPreviousModelVersionLoading || isLoadingProgress || isLoadingModel,
      }}
    >
      <>
        <FieldModelTitle
          fieldModel={fieldModel}
          publishStatus={publishStatus}
        />
        {editModelConfirmed && (
          <Section
            title={intl.formatMessage({
              id: 'ReviewPublish.reviewYourModel',
              defaultMessage: 'Review your model',
              description: 'Section title for reviewing the model',
            })}
          >
            <PublishOptions
              progress={progress}
              modelId={fieldModel.id}
              hasOnlyFiltersChanged={hasOnlyFiltersChanged}
              onStopPolling={setEnablePolling}
            />
          </Section>
        )}
        <Section
          title={intl.formatMessage({
            id: 'ReviewPublish.overviewOfYourModel',
            defaultMessage: 'Overview of your model',
            description: 'Section title for model overview',
          })}
        >
          <Accordion border="vertical" items={items} />
        </Section>
      </>
    </ContentContainer>
  );
};
